import { motion } from "framer-motion";
import { Scissors, Shapes, Clapperboard, Sparkles, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { scrollToId } from "@/lib/scrollTo";

const SERVICES = [
  {
    icon: Scissors,
    title: "Video Editing & Storytelling",
    desc: "Long-form essays, vlogs, podcasts, gaming cuts and documentaries — paced for retention, cut for story.",
    stack: ["Premiere Pro", "DaVinci Resolve"],
    meta: "Fast turnarounds · retention-first pacing",
    span: "lg:col-span-2",
    img: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    icon: Shapes,
    title: "2D & 3D Animation",
    desc: "Character animation, motion graphics, explainers and anime-style FX built frame by frame.",
    stack: ["After Effects", "Blender", "Cinema 4D"],
    meta: "From motion design to full anime pipelines",
  },
  {
    icon: Clapperboard,
    title: "Full Film & Commercial Production",
    desc: "Scripting, directing, shooting and sound design — a complete production unit on the ground.",
    stack: ["Scripting", "Direction", "Sound Design"],
    meta: "Concept to final master, handled end-to-end",
  },
  {
    icon: Sparkles,
    title: "Post-Production & VFX",
    desc: "CGI compositing, chroma keying, sound mastering, trailer cuts and signature color grades.",
    stack: ["VFX Compositing", "Color Grading", "Sound Mastering"],
    meta: "Cinema-grade finishing for every frame",
    span: "lg:col-span-2",
  },
];

export default function Services() {
  return (
    <section id="services" className="py-24 md:py-32 bg-[#F7F7F8] border-y border-gray-100 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-14">
          <SectionTag>What We Do</SectionTag>
          <h2 className="font-display font-extrabold text-gray-900 text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">
            ANY TYPE. ANY QUALITY.
            <br />
            <span className="text-crimson">ANY BUDGET.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} className={s.span}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="group relative h-full rounded-3xl border border-gray-100 bg-white shadow-soft p-7 md:p-9 overflow-hidden card-hover"
                data-testid={`service-card-${i + 1}`}
              >
                {s.img && (
                  <div className="absolute inset-0 opacity-[0.1] group-hover:opacity-[0.18] transition-opacity duration-700">
                    <img src={s.img} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-white/30" />
                  </div>
                )}
                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-crimson/10 border border-crimson/20 flex items-center justify-center text-crimson group-hover:bg-crimson group-hover:text-white transition-colors duration-500">
                      <s.icon className="w-5 h-5" />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest text-gray-300">0{i + 1}</span>
                  </div>
                  <h3 className="font-display font-bold text-gray-900 text-xl md:text-2xl mt-6 leading-snug">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mt-3">{s.desc}</p>
                  <div className="mt-auto pt-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {s.stack.map((t) => (
                        <span
                          key={t}
                          className="px-3 py-1 rounded-full border border-gray-200 bg-gray-50 font-mono text-[10px] tracking-wide text-gray-600"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <p className="font-mono text-[10px] tracking-widest text-gray-400 uppercase">{s.meta}</p>
                  </div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10 flex justify-center">
          <button
            type="button"
            data-testid="services-cta-button"
            onClick={() => scrollToId("brief")}
            className="group inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-crimson transition-colors duration-300"
          >
            Tell us what you need — we&apos;ll match the pipeline
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
